"use client";

import * as React from "react";

/**
 * Base UI's imperative popup actions. Declared structurally so this hook can
 * serve every popup family without importing their `Root.Actions` types —
 * Dialog, Sheet and AlertDialog all resolve to this exact shape, and a ref's
 * type is invariant, so it has to match member for member.
 */
export interface PopupExitActions {
  unmount: () => void;
  close: () => void;
}

/**
 * Finish a popup's exit transition when its owner's effects go away.
 *
 * A closing popup stays mounted until its exit transition finishes, and the
 * thing that notices the transition finished is a React effect. React
 * `<Activity mode="hidden">` destroys a subtree's effects while KEEPING the
 * subtree — and a popup is portaled to `<body>`, so hiding the panel never
 * reaches it. Hide a panel while a popup inside it is mid-exit and nothing is
 * left to unmount it: the backdrop stays in the body at `opacity: 0`,
 * invisible but full-screen and still taking pointer events, so every tap in
 * the app lands on nothing until a reload. Reachable in the funder workspace,
 * whose Review and Decide panels are `<Activity>` siblings — close an
 * evaluation and navigate back inside the 200ms exit.
 *
 * Forcing the unmount from the teardown covers the same-commit case too: a
 * Back that both drops the URL param owning the popup and switches the tab
 * closes and hides in one render, so there is no "it is closing" state a guard
 * could read.
 *
 * Two details are load-bearing:
 *
 * - The actions are captured at effect SETUP. By teardown, Base UI's own
 *   `useImperativeHandle` cleanup has already nulled the ref, so reading it
 *   there finds nothing and this silently does nothing.
 * - The unmount is SYNCHRONOUS. Deferring it (a microtask, a timer) schedules
 *   the state update after the hide has committed, and an update inside a
 *   hidden `<Activity>` subtree doesn't flush — the backdrop stays.
 *
 * Tearing down an OPEN popup this way is deliberate and cheap: on a real
 * unmount React is removing the DOM regardless, and on an `<Activity>` hide
 * the popup remounts from its own open state when the panel comes back —
 * better than the alternative, a live modal sitting over the tab the user
 * switched to. The one cost is under React StrictMode (dev in the Next apps),
 * where the mount → cleanup → mount cycle unmounts and remounts a popup that
 * rendered already-open, e.g. from a deep link. It recovers on the remount,
 * before paint, and only in dev.
 *
 * Pass the result as the popup Root's `actionsRef`.
 */
export function usePopupExitTeardown(
  forwarded?: React.RefObject<PopupExitActions | null> | null,
): React.RefObject<PopupExitActions | null> {
  const actions = React.useRef<PopupExitActions | null>(null);

  // Keep a caller-supplied `actionsRef` working: the wrapper has to own the
  // prop to install this teardown, so a forwarded ref would otherwise be
  // silently dropped.
  React.useEffect(() => {
    if (forwarded == null) return undefined;
    forwarded.current = actions.current;
    return () => {
      forwarded.current = null;
    };
  });

  React.useEffect(() => {
    const live = actions.current;
    return () => live?.unmount();
  }, []);

  return actions;
}
